import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Route as AdminLayoutRoute } from "./_layout";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import {
  AnomalyResolutionSlideOver,
  severityLabel,
  severityVariant,
  typeLabel,
} from "../../components/admin/AnomalyResolutionSlideOver";

// AnomalyResolutionSlideOver (mimo migrační seznam) stále vrací legacy BadgeVariant —
// přemapuj na nativní UUI Badge color, dokud nebude migrován i ten soubor.
type NativeBadgeColor = "gray" | "blue" | "success" | "warning" | "error";
function toBadgeColor(variant: string): NativeBadgeColor {
  switch (variant) {
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "success":
      return "success";
    case "info":
      return "blue";
    default:
      return "gray";
  }
}
import { useAdminAlertsSse } from "../../hooks/useAdminAlertsSse";
import { apiCall, ApiError } from "../../lib/apiClient";
import type {
  AnomalyListResponse,
  AnomalyResolutionInput,
  AnomalyRow,
  ResolveAnomalyResponse,
} from "../../schemas/anomalyResolution";

/**
 * UC-007 Phase 20 — Admin anomaly resolution route (R-035 + AT-013).
 *
 * Layout:
 *   1. Critical banner aria-live=assertive — visible jen pokud existuje
 *      nepřečtená CRITICAL anomaly v SSE stream (NFR-014).
 *   2. Filter chips (severity + type) + Data Table (pending nahoře, resolved dole).
 *   3. Klik na row → Slide-over s AnomalyResolutionSlideOver (REQ-SEC-019 reason ≥ 10).
 *
 * Pull fallback: /api/admin/anomalies refreshed po SSE event nebo manuálně.
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/anomalies",
  component: AdminAnomaliesPage,
});

type SeverityFilter = "all" | "critical" | "high" | "medium" | "low";

const SEVERITY_CHIPS: { id: SeverityFilter; label: string }[] = [
  { id: "all", label: "Vše" },
  { id: "critical", label: "Kritické" },
  { id: "high", label: "Vysoké" },
  { id: "medium", label: "Střední" },
  { id: "low", label: "Nízké" },
];

function AdminAnomaliesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<AnomalyListResponse>({
    queryKey: ["admin-anomalies"],
    queryFn: () => apiCall<AnomalyListResponse>("/api/admin/anomalies"),
    refetchInterval: 60_000,
  });
  const { snapshot } = useAdminAlertsSse({
    pullFallbackUrl: "/api/admin/anomalies",
  });

  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [activeAnomaly, setActiveAnomaly] = useState<AnomalyRow | null>(null);
  const [slideOpen, setSlideOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Whenever a new SSE event arrives → invalidate the list cache.
  // (Cheaper than re-fetching from the SSE event payload, since the row carries
  // additional joined data.)
  const sseSeq = snapshot.alerts.length;
  useMemo(() => {
    if (sseSeq > 0) {
      void queryClient.invalidateQueries({ queryKey: ["admin-anomalies"] });
    }
    return null;
  }, [sseSeq, queryClient]);

  const rows = useMemo<AnomalyRow[]>(() => {
    if (!data?.rows) return [];
    if (severityFilter === "all") return data.rows;
    return data.rows.filter((r) => r.severity === severityFilter);
  }, [data, severityFilter]);

  const criticalUnresolved = useMemo(() => {
    if (!data?.rows) return 0;
    return data.rows.filter(
      (r) => r.status === "pending" && r.severity === "critical",
    ).length;
  }, [data]);

  const resolveMutation = useMutation<
    ResolveAnomalyResponse,
    ApiError,
    { anomalyId: string; input: AnomalyResolutionInput }
  >({
    mutationFn: ({ anomalyId, input }) =>
      apiCall<ResolveAnomalyResponse>(`/api/admin/anomalies/${anomalyId}/resolve`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      setServerError(null);
      setSlideOpen(false);
      setActiveAnomaly(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-anomalies"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        switch (err.errorCode) {
          case "RESOLVE_REASON_REQUIRED":
            setServerError("Důvod musí mít alespoň 10 znaků.");
            return;
          case "RESOLVE_ACTION_INVALID":
            setServerError("Vybraná akce není povolená.");
            return;
          case "ANOMALY_NOT_FOUND":
            setServerError("Anomálie nebyla nalezena.");
            return;
          case "ANOMALY_ALREADY_RESOLVED":
            setServerError("Anomálie už byla vyřešena.");
            return;
          default:
            setServerError(err.message || "Uložení selhalo.");
            return;
        }
      }
      setServerError("Neznámá chyba sítě.");
    },
  });

  const columns: Column<AnomalyRow>[] = [
    {
      key: "severity",
      header: "Závažnost",
      cell: (r) => (
        <BadgeWithDot color={toBadgeColor(severityVariant(r.severity))}>
          {severityLabel(r.severity)}
        </BadgeWithDot>
      ),
    },
    {
      key: "type",
      header: "Typ",
      cell: (r) => typeLabel(r.anomaly_type),
    },
    {
      key: "amount",
      header: "Částka",
      cell: (r) => formatKc(r.amount_kc),
    },
    {
      key: "vs",
      header: "VS",
      cell: (r) => r.variable_symbol ?? "—",
    },
    {
      key: "payer",
      header: "Plátce",
      cell: (r) => r.counter_party_name ?? "—",
    },
    {
      key: "status",
      header: "Stav",
      cell: (r) => (
        <BadgeWithDot color={r.status === "pending" ? "warning" : "success"}>
          {r.status === "pending" ? "Otevřená" : "Vyřešená"}
        </BadgeWithDot>
      ),
    },
    {
      key: "created",
      header: "Vytvořeno",
      cell: (r) => formatDateTime(r.created_at),
    },
  ];

  function openRow(row: AnomalyRow): void {
    if (row.status === "resolved") return; // resolved rows are read-only
    setActiveAnomaly(row);
    setServerError(null);
    setSlideOpen(true);
  }

  function handleSubmit(anomalyId: string, input: AnomalyResolutionInput): void {
    resolveMutation.mutate({ anomalyId, input });
  }

  return (
    <section aria-labelledby="anomalies-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="anomalies-title" className="text-2xl font-bold text-primary m-0">
          Platební anomálie
        </h2>
        <p className="text-base text-tertiary m-0">
          Detekce neshod (cross-account, heuristické, duplicitní, neshoda částky) v reálném čase.
        </p>
      </header>

      {criticalUnresolved > 0 && (
        <InlineAlert
          variant="error"
          data-testid="anomalies-critical-banner"
        >
          <strong>{criticalUnresolved}</strong> kritická anomálie čeká na řešení — okamžitě prosím rozhodněte.
        </InlineAlert>
      )}

      {error && (
        <InlineAlert variant="error" title="Chyba">
          Načítání seznamu anomálií selhalo: {(error as Error).message}
        </InlineAlert>
      )}

      <div
        className="flex gap-2 flex-wrap"
        role="group"
        aria-label="Filter podle závažnosti"
      >
        {SEVERITY_CHIPS.map((chip) => {
          const active = severityFilter === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => setSeverityFilter(chip.id)}
              aria-pressed={active}
              data-testid={`anomaly-filter-${chip.id}`}
              className={
                "px-3 py-1.5 rounded-full text-sm font-medium ring-1 transition-colors " +
                (active
                  ? "bg-brand-solid text-white ring-transparent"
                  : "bg-primary text-secondary ring-secondary hover:bg-secondary")
              }
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      <DataTable<AnomalyRow>
        caption="Tabulka platebních anomálií"
        captionVisible={false}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.anomaly_id}
        emptyMessage="Žádné anomálie odpovídající filtru."
        onRowClick={openRow}
        loading={isLoading}
      />

      <AnomalyResolutionSlideOver
        open={slideOpen}
        anomaly={activeAnomaly}
        submitting={resolveMutation.isPending}
        serverError={serverError}
        onClose={() => {
          setSlideOpen(false);
          setActiveAnomaly(null);
          setServerError(null);
        }}
        onSubmit={handleSubmit}
      />
    </section>
  );
}

function formatKc(amount: number): string {
  return (
    amount.toLocaleString("cs-CZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " Kč"
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ");
  } catch {
    return iso;
  }
}
